/**
 * Puts the full-width block (the recommender) on a fixed row by reordering the list only.
 */

// Row 4 is the target: the tall ad ends on row 3, so the block fits below it without moving anything.
// This only holds while no tall ad reaches row 4 — see `isTargetRowTakenByTallAd` below.
const ROWS_BEFORE_FULL_WIDTH = 3

// Keep in sync with `$tall-ad-row-span` in ./index.scss — SCSS and JS cannot share a constant.
export const TALL_AD_ROW_SPAN = 2

// Experiment variant: the ad covers one more row. Keep in sync with `$tall-ad-row-span-three-cards`.
export const TALL_AD_ROW_SPAN_THREE_CARDS = 3

// `position` is the index of the card the ad is rendered AFTER, so the ad takes the next cell.
const getTallAdStartRow = (position, columnsCount) => Math.floor((position + 1) / columnsCount)

/**
 * How many cards fill the rows before the block. Not `rows × columns`: a tall ad spans
 * `TALL_AD_ROW_SPAN` rows in ONE column, filling cells the cards no longer have to — 7 cards + 2 ad
 * cells fill 3 rows of 3 columns, 10 + 2 fill 3 rows of 4.
 */
export const getFullWidthInjectionIndex = ({
  columnsCount,
  tallAdPositions,
  isTallAdVisible,
  tallAdRowSpan = TALL_AD_ROW_SPAN
}) => {
  const cells = ROWS_BEFORE_FULL_WIDTH * columnsCount

  // Below xl the ad is hidden and fills nothing
  if (!isTallAdVisible) return cells

  return tallAdPositions.reduce((remaining, position) => {
    const rowsInRange = ROWS_BEFORE_FULL_WIDTH - getTallAdStartRow(position, columnsCount)

    // Clamped because only the rows in range count: an ad starting on the first row covers as many of
    // them as its span, one starting on the last covers 1, and one starting below the block covers none
    // (negative here).
    return remaining - Math.max(0, Math.min(tallAdRowSpan, rowsInRange))
  }, cells)
}

/** True when a tall ad's span reaches the target row, which is what breaks the contract above. */
export const isTargetRowTakenByTallAd = ({
  columnsCount,
  tallAdPositions,
  isTallAdVisible,
  tallAdRowSpan = TALL_AD_ROW_SPAN
}) =>
  isTallAdVisible &&
  tallAdPositions.some(position => {
    const startRow = getTallAdStartRow(position, columnsCount)

    return startRow <= ROWS_BEFORE_FULL_WIDTH && startRow + tallAdRowSpan > ROWS_BEFORE_FULL_WIDTH
  })

/** Reorders the list so the full-width entries sit at that index. Same items, same card order. */
export const snapFullWidthItems = ({
  listOfAds,
  columnsCount,
  tallAdPositions,
  isTallAdVisible,
  tallAdRowSpan = TALL_AD_ROW_SPAN
}) => {
  const fullWidthItems = listOfAds.filter(item => item._adSpace)

  // One column is mobile, which keeps its own flow: the block stays at its injection point
  if (columnsCount === 1 || fullWidthItems.length === 0) return listOfAds

  const cards = listOfAds.filter(item => !item._adSpace)
  const injectionIndex = getFullWidthInjectionIndex({columnsCount, tallAdPositions, isTallAdVisible, tallAdRowSpan})

  // Too few cards to reach that row: snapping would leave the block hanging below the last card
  if (cards.length < injectionIndex) return listOfAds

  return [...cards.slice(0, injectionIndex), ...fullWidthItems, ...cards.slice(injectionIndex)]
}
